import React, { useState, useEffect } from "react";
import {
  Search,
  Edit,
  Trash2,
  UserPlus,
  Eye,
  EyeOff,
  Plus,
  X,
  ArrowLeft,
  CirclePlus,
  ChevronLeft,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./user.css";
import { BASE_URL } from "../apiConfig";
import { message } from "antd";
import { ReactComponent as Pencil } from "../../Images/pencil.svg";

const UserManagement = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [userType, setUserType] = useState([]);

  // Add new state variables for deletion modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [formData, setFormData] = useState({
    user_id: "",
    fullName: "",
    email: "",
    password: "",
    userType: "",
    profilePicture: null,
    profilePictureUrl: "",
    phoneNumber: "",
  });
  const [editFormData, setEditFormData] = useState({
    user_id: "",
    fullName: "",
    email: "",
    userType: "",
    profilePicture: null,
    profilePictureUrl: "",
    phoneNumber: "",
  });
  const [profilePreview, setProfilePreview] = useState(null);
  const [editProfilePreview, setEditProfilePreview] = useState(null);
  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    password: "",
    userType: "",
    profilePicture: "",
    phoneNumber: "",
  });

  // Fetch users data when component mounts
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("accessToken");

    try {
      const response = await fetch(`${BASE_URL}/user/get_all_users_with_client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddNewUser = () => {
    setModalVisible(true);
    // Reset form when opening modal
    setFormData({
      user_id: "",
      fullName: "",
      email: "",
      password: "",
      userType: "",
      profilePicture: null,
      profilePictureUrl: "",
      phoneNumber: "",
    });
    setProfilePreview(null);
    setErrors({
      fullName: "",
      email: "",
      password: "",
      userType: "",
      profilePicture: "",
      phoneNumber: "",
    });
  };

  const handleEditUser = (userId) => {
    try {
      // Find the user from the existing users array in state
      const userData = users.find((user) => user.user_id === userId);

      if (!userData) {
        throw new Error("User not found");
      }

      console.log("User details:", userData);

      // Set the form data with the user information
      setEditFormData({
        user_id: userData.user_id,
        fullName: `${userData.first_name} ${userData.last_name || ""}`.trim(),
        email: userData.email,
        userType: userData.user_type,
        profilePicture: null,
        profilePictureUrl: userData.image || "",
        phoneNumber: userData.phone_number || "",
      });
      console.log(userType);

      // Set the preview if an image exists
      if (userData.image) {
        setEditProfilePreview(userData.image);
      } else {
        setEditProfilePreview(null);
      }

      setEditModalVisible(true);
    } catch (error) {
      console.error("Error opening edit modal:", error);
      alert("Failed to load user details. Please try again.");
    }
  };

  // Updated delete user functions
  const handleDeleteButtonClick = (userId) => {
    setUserToDelete(userId);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    const token = localStorage.getItem("accessToken");

    try {
      const response = await fetch(
        `${BASE_URL}/user/delete_user?user_id=${userToDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: userToDelete }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete user");
      }
      if (response.ok) {
        fetchUsers();
        messageApi.success("User deleted successfully");
      }

      // Refresh users list after deletion
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user. Please try again.");
    } finally {
      // Close the modal after deletion attempt
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field changed: ${name}, value: ${value}`);
    setFormData({
      ...formData,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    console.log(`Edit field changed: ${name}, value: ${value}`); // Add this for debugging
    setEditFormData({
      ...editFormData,
      [name]: value,
    });

    // Clear error for this field when user starts typing
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const uploadProfilePicture = async (file, isEdit) => {
    setIsUploading(true);

    try {
      const token = localStorage.getItem("accessToken");

      // Create headers
      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      // Create FormData object and append the file
      const formData = new FormData();
      formData.append("image_logo", file);

      // Request options
      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      };

      // Send the request to the upload endpoint with the document_type query parameter
      const response = await fetch(
        `${BASE_URL}/upload_picture?document_type=user`,
        requestOptions
      );

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      // Parse the response
      const result = await response.json();

      // Store the returned image URL in state
      if (isEdit) {
        setEditFormData((prevData) => ({
          ...prevData,
          profilePictureUrl: result.filename,
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          profilePictureUrl: result.filename,
        }));
      }

      return result.filename;
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      setErrors((prevErrors) => ({
        ...prevErrors,
        profilePicture: "Failed to upload image. Please try again.",
      }));
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        profilePicture: "Please select a valid image file.",
      }));
      return;
    }

    // Allowed file formats
    const allowedFormats = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedFormats.includes(file.type)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        profilePicture:
          "Invalid file format. Only JPEG, JPG and PNG are allowed.",
      }));
      return;
    }

    // Store the file for preview only
    setFormData((prevData) => ({
      ...prevData,
      profilePicture: file,
    }));

    // Create URL for preview only
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setErrors((prevErrors) => ({
      ...prevErrors,
      profilePicture: "",
    }));

    // Upload the image file immediately after selection
    await uploadProfilePicture(file, false);
  };

  const handleEditProfilePictureChange = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        profilePicture: "Please select a valid image file.",
      }));
      return;
    }

    // Allowed file formats
    const allowedFormats = ["image/jpeg", "image/png", "image/jpg"];
    if (!allowedFormats.includes(file.type)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        profilePicture:
          "Invalid file format. Only JPEG, JPG and PNG are allowed.",
      }));
      return;
    }

    // Store the file for preview only
    setEditFormData((prevData) => ({
      ...prevData,
      profilePicture: file,
    }));

    // Create URL for preview only
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditProfilePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setErrors((prevErrors) => ({
      ...prevErrors,
      profilePicture: "",
    }));

    // Upload the image file immediately after selection
    await uploadProfilePicture(file, true);
  };

  const validateForm = (data, isEdit = false) => {
    let isValid = true;
    const newErrors = {
      fullName: "",
      email: "",
      password: "",
      userType: "",
      profilePicture: "",
      phoneNumber: "",
    };

    // Validate full name
    if (!data.fullName || !data.fullName.trim()) {
      newErrors.fullName = "This field cannot be empty";
      isValid = false;
    } else if (data.fullName.trim().length < 3) {
      newErrors.fullName =
        "Minimum Character Limit not met, please contact Admin";
      isValid = false;
    } else if (data.fullName.trim().length > 50) {
      newErrors.fullName =
        "Maximum Character Limit Exceeded, please recheck or contact Admin";
      isValid = false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email || !data.email.trim()) {
      newErrors.email = "This field cannot be empty";
      isValid = false;
    } else if (!emailRegex.test(data.email.trim())) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate phone number
    if (!isEdit) {
      const phoneRegex = /^\+?[0-9]{10}$/;
      if (!data.phoneNumber || !data.phoneNumber.trim()) {
        newErrors.phoneNumber = "This field cannot be empty";
        isValid = false;
      } else if (!phoneRegex.test(data.phoneNumber.trim())) {
        newErrors.phoneNumber = "Please enter a valid 10 digit phone number";
        isValid = false;
      }
    } else if (data.phoneNumber && data.phoneNumber.trim()) {
      // If phone number is provided during edit, validate it
      const phoneRegex = /^\+?[0-9]{10}$/;
      if (!phoneRegex.test(data.phoneNumber.trim())) {
        newErrors.phoneNumber = "Please enter a valid phone number 10 digit";
        isValid = false;
      }
    }

    // Validate password (only for new users)
    if (!isEdit) {
      if (!data.password || !data.password.trim()) {
        newErrors.password = "This field cannot be empty";
        isValid = false;
      } else if (data.password.trim().length < 6) {
        newErrors.password = "Password must be at least 6 characters";
        isValid = false;
      }
    }

    // Validate user type
    if (!data.userType) {
      newErrors.userType = "This field cannot be empty";
      isValid = false;
    }

    // For edit form, we don't require a new profile picture if one already exists
    if (!isEdit && !data.profilePictureUrl) {
      newErrors.profilePicture = "Profile picture is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm(formData)) {
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");

      // Extract first and last name from fullName
      const nameParts = formData.fullName.trim().split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      const requestBody = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        password: formData.password,
        client_id: null,
        user_type: formData.userType,
        image_url: formData.profilePictureUrl,
        phone_number: formData.phoneNumber,
      };

      console.log("Create user request payload:", requestBody);

      // Make POST request to create user
      const response = await fetch(`${BASE_URL}/user/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        // const errorData = await response.json().catch(() => ({}));
        // throw new Error(`Failed to create client. Status: ${response.status}. ${errorData.message || ''}`);
        messageApi.error(data.detail);
      } else {
        fetchUsers();
        resetFormAndCloseModal();
        // alert("User created successfully");
        messageApi.success("User created successfully");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      alert("Failed to create user. Please try again.");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    console.log("Submit button clicked");

    // Validate form before submission
    if (!validateForm(editFormData, true)) {
      return;
    }
    console.log(" clicked");

    try {
      const token = localStorage.getItem("accessToken");

      // Extract first and last name from fullName
      const nameParts = editFormData.fullName.trim().split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

      // Create base request body with required fields
      const requestBody = {
        user_id: editFormData.user_id,
        first_name: firstName,
        last_name: lastName,
        email: editFormData.email,
        user_type: editFormData.userType,
        phone_number: editFormData.phoneNumber,
      };

      // Only add image if available
      if (editFormData.profilePictureUrl || editProfilePreview) {
        requestBody.image =
          editFormData.profilePictureUrl || editProfilePreview;
      }

      // Only add phone number if it exists in the form data
      // if (editFormData.phoneNumber) {
      //     requestBody.phone_number = editFormData.phoneNumber;
      // }

      console.log("Update request payload:", requestBody);

      // Make POST request to update user
      const response = await fetch(`${BASE_URL}/user/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        messageApi.error(data.detail);
      } else {
        fetchUsers();
        closeEditModal();
        messageApi.success("User updated successfully");
      }
    } catch (error) {
      console.error("Error creating client:", error);
      if (error.status === 409) {
        messageApi.error("Duplicate entry, please enter another name");
      } else if (error.status === 413) {
        messageApi.error("Image size too large, please upload a smaller image");
      } else {
        messageApi.error("An error occurred while adding the role");
      }
    }
  };

  const resetFormAndCloseModal = () => {
    setFormData({
      fullName: "",
      email: "",
      password: "",
      userType: "",
      phoneNumber: "",
      profilePicture: null,
      profilePictureUrl: "",
    });
    setProfilePreview(null);
    setShowPassword(false);
    setModalVisible(false);
    setErrors({
      fullName: "",
      email: "",
      password: "",
      userType: "",
      profilePicture: "",
      phoneNumber: "",
    });
  };

  const closeEditModal = () => {
    setEditFormData({
      user_id: "",
      fullName: "",
      email: "",
      userType: "",
      profilePicture: null,
      profilePictureUrl: "",
      phoneNumber: "",
    });
    setEditProfilePreview(null);
    setEditModalVisible(false);
    setErrors({
      fullName: "",
      email: "",
      password: "",
      userType: "",
      profilePicture: "",
      phoneNumber: "",
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      (user.first_name + " " + (user.last_name || ""))
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
      //    ||
      // user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Function to determine full name of user
  const getFullName = (user) => {
    return `${user.first_name} ${user.last_name || ""}`.trim();
  };

  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          throw new Error("No auth token found");
        }
        const response = await fetch(`${BASE_URL}/user/get_user_types`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        console.log(data);
        setUserType(data);
        console.log(userType);
      } catch (error) {
        console.error("Error fetching user types:", error);
      }
    };

    fetchUserTypes();
  }, []);

  // useEffect(() => {
  //     console.log("Updated userType:", userType);
  // }, [userType]);

  return (
    <>
      {contextHolder}
      <div className="user-sidebar"></div>

      <div className="user-container">
        <div className="user-inside-container">
          <div className="user-header">
            <h1 className="user-title">Users</h1>
            <button className="user-add-button" onClick={handleAddNewUser}>
              {/* <UserPlus size={16} /> */}

              <span>Add New User</span>
            </button>
          </div>

          <div className="user-search-box">
            <input
              type="text"
              placeholder="Search User...."
              value={searchTerm}
              onChange={handleSearchChange}
              className="user-search-input"
            />
            <button className="user-search-button">
              <Search size={16} />
            </button>
          </div>

          <div className="user-cards-container">
            {isLoading ? (
              <div className="user-loading">Loading users...</div>
            ) : filteredUsers.length === 0 ? (
              <div className="user-no-results">No users found</div>
            ) : (
              filteredUsers.map((user) => (
                <div key={user.user_id} className="user-card">
                  {/* Role tag at top right */}
                  <div className="user-role">{user.user_type}</div>

                  <div className="user-left-section">
                    <div className="user-avatar">
                      {user.image ? (
                        <img
                          src={user.image}
                          alt={`${getFullName(user)} avatar`}
                        />
                      ) : (
                        <div className="user-avatar-placeholder">
                          {user.first_name.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="user-name">{getFullName(user)}</div>
                  </div>

                  {/* Action buttons at bottom right */}
                  <div className="user-actions">
                    <button
                      className="user-edit-button"
                      onClick={() => handleEditUser(user.user_id)}
                    >
                      {/* <Edit size={18} /> */}
                      <Pencil size={16} />
                    </button>
                    <button
                      className="user-delete-button"
                      onClick={() => handleDeleteButtonClick(user.user_id)}
                    >
                      <Trash2 size={18} color="#d32f2f" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Create User Modal */}
          {modalVisible && (
            <div className="user-modal-overlay">
              <div className="user-modal-content">
                <div className="user-modal-header">
                  <h2 className="user-modal-title">Create Users</h2>
                  <button
                    className="user-modal-close"
                    onClick={resetFormAndCloseModal}
                  >
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="user-modal-body">
                  {/* Profile Picture Upload */}
                  <div className="user-profile-upload">
                    <label
                      htmlFor="profilePicture"
                      className="user-profile-label"
                    >
                      <div className="user-profile-circle">
                        {profilePreview ? (
                          <div className="user-profile-preview-container">
                            <img
                              src={profilePreview}
                              alt="Profile Preview"
                              className="user-profile-preview"
                            />
                            {isUploading && (
                              <div className="user-profile-upload-overlay">
                                Uploading...
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="user-profile-placeholder">
                            <Plus size={24} />
                            <span>Profile Picture</span>
                            {isUploading && (
                              <div className="user-profile-uploading">
                                Uploading...
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                    <input
                      type="file"
                      id="profilePicture"
                      name="profilePicture"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleProfilePictureChange}
                      className="user-profile-input"
                      disabled={isUploading}
                    />
                    {errors.profilePicture && (
                      <div
                        className="user-error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.profilePicture}
                      </div>
                    )}
                    <p className="user-file-support">
                      File supported: jpg, jpeg or png{" "}
                      <span style={{ color: "red" }}>*</span>
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="user-form-group">
                    <label className="user-form-label">
                      Full Name<span className="user-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleFormChange}
                      placeholder="Enter Full Name"
                      className={`user-form-input ${
                        errors.fullName ? "input-error" : ""
                      }`}
                    />
                    {errors.fullName && (
                      <div
                        className="user-error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.fullName}
                      </div>
                    )}
                  </div>

                  <div className="user-form-group">
                    <label className="user-form-label">
                      Email-ID<span className="user-required">*</span>
                    </label>
                    <input
                      autoComplete="off"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="Enter Email-ID"
                      className={`user-form-input ${
                        errors.email ? "input-error" : ""
                      }`}
                    />
                    {errors.email && (
                      <div
                        className="user-error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="user-form-group">
                    <label className="user-form-label">
                      Phone Number<span className="user-required">*</span>
                    </label>
                    <input
                      autoComplete="off"
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleFormChange}
                      placeholder="Enter Phone Number"
                      className={`user-form-input ${
                        errors.phoneNumber ? "input-error" : ""
                      }`}
                    />
                    {errors.phoneNumber && (
                      <div
                        className="user-error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.phoneNumber}
                      </div>
                    )}
                  </div>

                  <div className="user-form-group">
                    <label className="user-form-label">
                      Password<span className="user-required">*</span>
                    </label>
                    <div className="user-password-container">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleFormChange}
                        placeholder="Enter Password"
                        className={`user-form-input ${
                          errors.password ? "input-error" : ""
                        }`}
                      />
                      <button
                        type="button"
                        className="user-password-toggle"
                        onClick={togglePasswordVisibility}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <div
                        className="user-error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <div className="user-form-group">
                    <label className="user-form-label">
                      User Type<span className="user-required">*</span>
                    </label>
                    <div className="user-select-container">
                      <select
                        name="userType"
                        value={formData.userType}
                        onChange={handleFormChange}
                        className={`user-form-select ${
                          errors.userType ? "input-error" : ""
                        }`}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        {userType.map((type, index) => (
                          <option key={index} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.userType && (
                      <div
                        className="user-error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.userType}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="user-form-actions">
                    <button
                      type="button"
                      className="user-discard-button"
                      onClick={resetFormAndCloseModal}
                    >
                      Discard
                    </button>
                    <button
                      type="submit"
                      className="user-create-button"
                      disabled={isUploading}
                    >
                      Create
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit User Modal */}
          {editModalVisible && (
            <div className="user-modal-overlay">
              <div className="user-modal-content">
                <div className="user-modal-header">
                  <button className="user-back-button" onClick={closeEditModal}>
                    {/* <ChevronLeft
                      size={30}
                      className="user-back-icon"
                      style={{ paddingTop: "12px", paddingTop: "14px" }}
                    /> */}
                    <span className="user-header-title">Edit User</span>
                  </button>
                  <button className="user-modal-close" onClick={closeEditModal}>
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleEditSubmit} className="user-modal-body">
                  {/* Profile Picture Upload */}
                  <div className="user-profile-upload">
                    <label
                      htmlFor="editProfilePicture"
                      className="user-profile-label"
                    >
                      <div className="user-profile-circle">
                        {editProfilePreview ? (
                          <div className="user-profile-preview-container">
                            <img
                              src={editProfilePreview}
                              alt="Profile Preview"
                              className="user-profile-preview"
                            />
                            {isUploading && (
                              <div className="user-profile-upload-overlay">
                                Uploading...
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="user-profile-placeholder">
                            <Plus size={24} />
                            <span>Profile Picture</span>
                            {isUploading && (
                              <div className="user-profile-uploading">
                                Uploading...
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                    <input
                      type="file"
                      id="editProfilePicture"
                      name="profilePicture"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleEditProfilePictureChange}
                      className="user-profile-input"
                      disabled={isUploading}
                    />
                    {errors.profilePicture && (
                      <div
                        className="error-message"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {errors.profilePicture}
                      </div>
                    )}
                    <p className="user-file-support">
                      File supported: jpg, jpeg or png
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="user-form-group">
                    <label className="user-form-label">
                      Full Name<span className="user-required">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={editFormData.fullName}
                      onChange={handleEditFormChange}
                      placeholder="Enter Full Name"
                      className="user-form-input"
                      required
                    />
                    {errors.fullName && (
                      <div
                        className="error-message"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {errors.fullName}
                      </div>
                    )}
                  </div>

                  <div className="user-form-group">
                    <label className="user-form-label">
                      Email-ID<span className="user-required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={editFormData.email}
                      onChange={handleEditFormChange}
                      placeholder="Enter Email-ID"
                      className="user-form-input"
                      required
                    />
                    {errors.email && (
                      <div
                        className="error-message"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="user-form-group">
                    <label className="user-form-label">
                      Phone Number<span className="user-required">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={editFormData.phoneNumber}
                      onChange={handleEditFormChange}
                      placeholder="Enter Phone Number"
                      className={`user-form-input ${
                        errors.phoneNumber ? "input-error" : ""
                      }`}
                    />
                    {errors.phoneNumber && (
                      <div
                        className="error-message"
                        style={{ color: "red", fontSize: "10px" }}
                      >
                        {errors.phoneNumber}
                      </div>
                    )}
                  </div>

                  {/* <div className="user-form-group">
                    <label className="user-form-label">
                        Password<span className="user-required">*</span>
                    </label>
                    <input
                        type="password"
                        name="password"
                        value={editFormData.password}
                        onChange={handleEditFormChange}
                        placeholder="Enter Password"
                        className="user-form-input"
                        required
                    />
                    {errors.password && (
                        <div className="error-message" style={{ color: 'red', fontSize: '12px' }}>
                            {errors.password}
                        </div>
                    )}
                </div> */}

                  <div className="user-form-group">
                    <label className="user-form-label">
                      User Type<span className="user-required">*</span>
                    </label>
                    <div className="user-select-container">
                      <select
                        name="userType"
                        value={editFormData.userType}
                        onChange={handleEditFormChange}
                        className="user-form-select"
                        required
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        {/* <option value="admin">Admin</option>
                                            <option value="Project Architect">Project Architect</option> */}
                        {userType.map((type, index) => (
                          <option key={index} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.userType && (
                      <div
                        className="error-message"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {errors.userType}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="user-form-actions">
                    <button
                      type="button"
                      className="user-discard-button"
                      onClick={closeEditModal}
                    >
                      Discard
                    </button>
                    <button
                      type="submit"
                      className="user-create-button"
                      disabled={isUploading}
                    >
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showDeleteModal && (
            <div className="role-delete-modal-overlay">
              <div className="role-delete-modal-container">
                <div className="role-delete-modal-content">
                  <h3>Confirm Delete User?</h3>
                  <div className="role-delete-modal-buttons">
                    <button
                      className="role-delete-modal-button role-delete-cancel"
                      onClick={cancelDelete}
                    >
                      No
                    </button>
                    <button
                      className="role-delete-modal-button role-delete-confirm"
                      onClick={confirmDeleteUser}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UserManagement;
